<template>
  <ForwardsPageComponent>
    <div class="mb-4">
      <h2 class="mb-1">{{ $t("forwards.overview.title") }}</h2>
      <p class="text-body-tertiary mb-0">
        {{ $t("forwards.overview.description") }}
      </p>
    </div>

    <!-- One full-width column, in the sketch's order (1a): an attention row is a full-width row
         with its CTA at the right, and the deliveries card is a table — neither survives being
         squeezed into half the page. -->
    <NeedsAttentionCardComponent
      :rows="store.attentionRows"
      :loading="store.overviewLoading"
      :error="store.overviewError"
      class="mb-3"
      @retry="store.loadOverview()"
    />

    <ForwardYearTotalsComponent
      :totals="store.yearTotalRows"
      :loading="store.yearTotalsLoading"
      :error="store.yearTotalsError"
      class="mb-3"
      @retry="store.loadYearTotals()"
    />

    <UpcomingDeliveriesCardComponent
      v-model:filters="store.deliveryFilters"
      :rows="store.upcomingDeliveries"
      :loading="store.deliveriesLoading"
      :error="store.deliveriesError"
      class="mb-3"
      @retry="store.loadDeliveries()"
    />

    <ManagedDealsTableComponent
      v-model:filters="store.dealFilters"
      :rows="store.dealRows"
      :loading="store.dealsLoading"
      :error="store.dealsError"
      :showsCreateLinks
      class="mb-3"
      @retry="store.loadDeals()"
    />

    <!-- The attention rows change with a mandate edited from this page. -->
    <MandateEditorModalHost @written="store.load()" />
  </ForwardsPageComponent>
</template>

<script setup lang="ts">
import { useAccountStore } from "@/composables/account/useAccountStore";
import ForwardsPageComponent from "@/forwards2/components/ForwardsPageComponent.vue";
import { disposePageStore } from "@/forwards2/disposePageStore";
import { isDomainError } from "@/forwards2/forwardsApi";
import { readsForwardsAsStaff } from "@/forwards2/forwardsGuard";
import ForwardYearTotalsComponent from "@/forwards2/overview/ForwardYearTotalsComponent.vue";
import ManagedDealsTableComponent from "@/forwards2/overview/ManagedDealsTableComponent.vue";
import NeedsAttentionCardComponent from "@/forwards2/overview/NeedsAttentionCardComponent.vue";
import UpcomingDeliveriesCardComponent from "@/forwards2/overview/UpcomingDeliveriesCardComponent.vue";
import { useOverviewStore } from "@/forwards2/overview/useOverviewStore";
import MandateEditorModalHost from "@/forwards2/mandates/MandateEditorModalHost.vue";
import { computed, onMounted, onBeforeUnmount, watch } from "vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

const store = useOverviewStore();
const accountStore = useAccountStore();
const { globalErrorHandler } = useGlobalHandlers();

// No load of its own: the admin guard loaded the account on entering the page, and this only picks
// which links to offer — each link's target judges the account again in its own guard. A sales
// admin is offered the create pages too and opens them read-only (user decision, 2026-09-28).
const showsCreateLinks = computed(() =>
  readsForwardsAsStaff(accountStore.account)
);

// A domain error is a sentence the user can act on and each card renders its own; anything else is
// a bug or an outage and belongs in the app-wide handler as well. Only the error that changed is
// forwarded: they are watched together, so a later deals failure must not re-report the
// unchanged overview one.
watch(
  [
    () => store.overviewError,
    () => store.deliveriesError,
    () => store.dealsError,
    () => store.yearTotalsError,
  ],
  (errors, previousErrors) => {
    for (const [index, error] of errors.entries()) {
      if (error === previousErrors[index]) continue;
      if (error !== null && !isDomainError(error)) globalErrorHandler(error);
    }
  }
);

onMounted(() => {
  void store.load();
});

// The store holds the route's query values and watches them. It outlives this page unless it is
// disposed, and would then refetch on every unrelated navigation; its state goes too, so the next
// visit does not open on this one's cards.
onBeforeUnmount(() => disposePageStore(store));
</script>
